// Search-box component paired with `useListSearch`. Renders an
// inline-styled input with a clear (×) button on the right when
// active. Used at the top of the admin lists (Tagging Rules, File
// Workflows, Storage Containers) so all three feel like the same
// surface.
//
// Style: deliberately spartan and visually similar to the existing
// page-subtitle so it reads as chrome, not content. Matches the
// look of the existing #nest-filter banner already on these pages.

import { Search, X } from 'lucide-react';

export interface ListSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Total row count before search filtering (for the "showing N of M" hint). */
  totalCount?: number;
  /** Visible row count after search filtering. */
  filteredCount?: number;
}

export function ListSearchInput({
  value,
  onChange,
  placeholder = 'Search by name or description…',
  totalCount,
  filteredCount,
}: ListSearchInputProps) {
  const active = value.trim().length > 0;
  const showHint = active && typeof totalCount === 'number' && typeof filteredCount === 'number';

  return (
    <div
      className="list-search"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          flex: '0 0 360px',
          maxWidth: '100%',
        }}
      >
        <Search
          size={14}
          strokeWidth={1.75}
          aria-hidden
          style={{
            position: 'absolute',
            left: 8,
            color: 'var(--text-muted)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label="Search list"
          style={{
            width: '100%',
            padding: '6px 28px 6px 28px',
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface-canvas)',
            fontSize: 13,
          }}
        />
        {active && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Clear search"
            title="Clear search"
            style={{
              position: 'absolute',
              right: 6,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 2,
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <X size={14} strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </div>
      {showHint && (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Showing {filteredCount} of {totalCount}
        </span>
      )}
    </div>
  );
}
